import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  getAllCustomSearchs,
  getACustomSearchsById,
  getCompanyTestgroups,
} from '../API/services/screeningService';

const QUERY_KEYS = {
  RECRUIT_SESSIONS: 'recruitSessions',
  TEST_GROUPS: 'testGroups',
};

// Hook to fetch all recruit sessions (evaluations) for a workspace
export const useRecruitSessions = (workspaceId, filters = {}, options = {}) =>
  useQuery({
    queryKey: [QUERY_KEYS.RECRUIT_SESSIONS, workspaceId, filters],
    queryFn: async () => {
      if (filters.screeningId) {
        return getACustomSearchsById(filters.screeningId);
      }

      const result = await getAllCustomSearchs({
        offset: 0,
        limit: 200,
        status: 'IN_PROGRESS',
        ...filters,
      });

      if (filters.excludeScreeningId && result?.collection) {
        return {
          ...result,
          collection: result.collection.filter(
            (screening) => screening.id !== filters.excludeScreeningId
          ),
        };
      }

      return result;
    },
    enabled: !!workspaceId || !!filters.screeningId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });

// Hook to fetch recruit sessions with infinite scroll
export const useRecruitSessionsInfinite = (workspaceId, filters = {}, options = {}) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEYS.RECRUIT_SESSIONS, 'infinite', workspaceId, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const queryParams = {
        offset: pageParam,
        limit: 20,
        status: 'IN_PROGRESS',
        ...filters,
      };

      if (pageParam === 0 && filters.initialIds) {
        queryParams.initialIds = filters.initialIds;
      }

      const result = await getAllCustomSearchs(queryParams);
      const containsId = result.collection?.some((s) => s.recruitSessionId === filters.screeningId);
      if (!containsId && pageParam === 0 && filters.screeningId) {
        const selectedOne = await getACustomSearchsById(filters.screeningId);
        if (selectedOne && selectedOne.id && selectedOne.stages?.[0]) {
          const firstStage = selectedOne.stages[0];
          const transformedSelectedOne = {
            recruitSessionId: selectedOne.id,
            recruitSessionStageId: firstStage.id,
            recruitSessionStageName: firstStage.name,
            recruitSessionStageStartDate: firstStage.startDate || null,
            recruitSessionStageEndDate: firstStage.endDate || null,
            recruitSessionStageEmptyDates: !firstStage.startDate && !firstStage.endDate,
            proctoringEnabled: firstStage.proctoringEnabled || false,
            proctoringAIEnabled: firstStage.proctoringAIEnabled || false,
            screeningId: firstStage.levelId,
            levelId: firstStage.levelId,
            partnerFirstName: '',
            partnerLastName: '',
            invitationsSent: '0',
            createdAccounts: '0',
            evaluationsStarted: '0',
            evaluationsFinished: '0',
            evaluationsPendingCredits: '0',
            invitationLink: '',
            id: selectedOne.id,
            isActive: true,
            name: selectedOne.name,
            stages: selectedOne.stages,
          };

          result.collection = [transformedSelectedOne, ...result.collection];
        }
      }

      if (filters.excludeScreeningId && result?.collection) {
        return {
          ...result,
          collection: result.collection.filter(
            (screening) => screening.id !== filters.excludeScreeningId
          ),
        };
      }

      return result;
    },
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage) return undefined;

      // Check if pagination object exists with nextPage property
      if (lastPage.pagination?.nextPage) {
        // Extract offset from nextPage URL if it exists
        try {
          const url = new URL(lastPage.pagination.nextPage, window.location.origin);
          const offset = url.searchParams.get('offset');
          return offset ? parseInt(offset, 10) : undefined;
        } catch {
          return undefined;
        }
      }

      // Fallback: calculate offset based on collection length
      const currentOffset = pages.length * 20;
      const hasMore = lastPage?.collection?.length === 20;
      return hasMore ? currentOffset : undefined;
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });

// Hook to fetch test groups with infinite scroll
export const useTestGroupsInfinite = (params = {}, options = {}) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEYS.TEST_GROUPS, params],
    queryFn: ({ pageParam = 0 }) => {
      const queryParams = {
        ...params,
        limit: 20,
      };

      // Use pageParam as offset (can be a number or extracted from URL)
      if (typeof pageParam === 'number') {
        queryParams.offset = pageParam;
      } else if (pageParam) {
        // If pageParam is a URL string, extract offset
        try {
          const url = new URL(pageParam, window.location.origin);
          const offset = url.searchParams.get('offset');
          if (offset) {
            queryParams.offset = parseInt(offset, 10);
          }
        } catch {
          queryParams.offset = 0;
        }
      }

      return getCompanyTestgroups(queryParams);
    },
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage) return undefined;

      // Check if pagination object exists with nextPage property
      if (lastPage.pagination?.nextPage) {
        // Extract offset from nextPage URL if it exists
        try {
          const url = new URL(lastPage.pagination.nextPage, window.location.origin);
          const offset = url.searchParams.get('offset');
          return offset ? parseInt(offset, 10) : undefined;
        } catch {
          return undefined;
        }
      }

      // Fallback: calculate offset based on collection length
      const currentOffset = pages.length * 20;
      const hasMore = lastPage?.collection?.length === 20;
      return hasMore ? currentOffset : undefined;
    },
    enabled: !!params.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });

// Hook to fetch test groups for a specific screening
export const useTestGroupsByScreening = (screeningId, options = {}) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEYS.TEST_GROUPS, 'byScreening', screeningId],
    queryFn: ({ pageParam = 0 }) =>
      getCompanyTestgroups({
        screeningId,
        offset: pageParam,
        limit: 20,
      }),
    getNextPageParam: (lastPage, pages) => {
      const currentOffset = pages.length * 20;
      const hasMore = lastPage?.collection?.length === 20;
      return hasMore ? currentOffset : undefined;
    },
    enabled: !!screeningId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
